import * as SplashScreen from 'expo-splash-screen';
import { memo, ReactNode, useMemo } from 'react';
import { Dimensions, useWindowDimensions } from 'react-native';
import {
  initialWindowMetrics,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { makeDebug } from '../utils/makeDebug';
import { isDebugEnabled } from './debugging';
import { performanceTracking } from './debugging/crashTracking';
import { Span, Transaction } from './debugging/crashTracking.shared';

const debug = makeDebug('app:launch', isDebugEnabled('launch'));

let transaction: Transaction | undefined;
let lastSpan: Span | undefined;
let startTimestamp = 0;
let lastSpanStartTimestamp = 0;

export const launchBegin = (options?: {
  preventSplashScreenAutoHide?: boolean;
}) => {
  debug('begin... ');
  startTimestamp = performance.now();

  transaction = performanceTracking.startTransaction({
    name: 'applaunch',
  });

  if (options?.preventSplashScreenAutoHide) {
    SplashScreen.preventAutoHideAsync()
      .then(res => {
        debug('splashscreen autohide prevented: ', res);
      })
      .catch(err => {
        console.error('error prevent splashscreen from auto hide: ', err);
      });
  }
};

export const launchEnd = () => {
  transaction?.finish();
  const elapsed = performance.now() - startTimestamp;
  debug(`finished, taken ${elapsed} (ms)`);
  void SplashScreen.hideAsync();
};

export const beginLaunchTrackingSpan = (name: string) => {
  debug(`${name} begin`);
  lastSpan = transaction?.startChild({ op: name });
  lastSpanStartTimestamp = performance.now();
};

export const endLaunchTrackingSpan = (name: string) => {
  const elapsed = performance.now() - lastSpanStartTimestamp;
  debug(`${name} end, taken ${elapsed} (ms)`);
  lastSpan?.finish();
  lastSpan = undefined;
};

export const LaunchTrackBeginSpan = memo(
  ({ name, children }: { name: string; children?: ReactNode }) => {
    useMemo(() => {
      beginLaunchTrackingSpan(name);
    }, [name]);
    return <>{children}</>;
  },
);

export const LaunchTrackEndSpan = memo(
  ({ name, children }: { name: string; children?: ReactNode }) => {
    useMemo(() => {
      endLaunchTrackingSpan(name);
    }, [name]);
    return <>{children}</>;
  },
);

export const LaunchTrackFinish = memo(
  ({ children }: { children?: ReactNode }) => {
    const insets = useSafeAreaInsets();
    const dimens = useWindowDimensions();
    const window = Dimensions.get('window');
    const screen = Dimensions.get('screen');
    debug('🟢 screen metrics: %O', {
      insets,
      dimens,
      window,
      screen,
      initialWindowMetrics,
    });
    useMemo(() => {
      launchEnd();
    }, []);
    return <>{children}</>;
  },
);
